import type { UserRetrieveResponse } from '@fireflyhealth/api'
import { Provider, providerCanProvideClinicalCareToPatient } from '~/entities/provider'
import { AssigneeGroupListItem } from './AssigneeGroupService'
export interface AssigneeGroupOption {
  label: string
  value: number | null // the Assignee Group id
  disabledReason?: string | null
  category: string
}

// The category the assignable group options carry; `UserGroupSelect`'s detailed variant
// groups options by it.
const GROUPS_CATEGORY = 'Groups'

const DISABLED_REASONS = {
  out_of_state: "Not licensed in the patient's state",
  inactive: 'Inactive',
  deprecated: 'Retired — reassign',
}

/**
 * Option representing a deprecated group that a work unit is *already* assigned to.
 *
 * Deprecated groups are left out of the assignable options, but a work unit still pointing at one
 * has to render its name rather than a blank owner, so the current value is offered back as a
 * disabled option: visible and labelled, but not selectable for a new assignment.
 */
export const deprecatedGroupOption = (group: AssigneeGroupListItem): AssigneeGroupOption => ({
  label: group.name,
  value: group.id,
  disabledReason: DISABLED_REASONS['deprecated'],
  // Must match the category the other group options carry: `UserGroupSelect`'s detailed variant
  // groups by this field, so a different value would render its own stray header.
  category: GROUPS_CATEGORY,
})

// Returns a list of grouped options, meant to be used in a react-select component
// Each individual option is a AssigneeGroup who a Task may be assigned to
export const getAssignableUserGroups = (
  providers: Provider[],
  canAssignToPatient: boolean,
  careTeam: number[] | undefined,
  patient?: UserRetrieveResponse | null,
  assigneeGroups?: AssigneeGroupListItem[] | null,
): {
  label: string
  options: AssigneeGroupOption[]
}[] => {
  const disabledReason = (userOption: Provider): string | null => {
    if (patient && !providerCanProvideClinicalCareToPatient(userOption, patient)) {
      return DISABLED_REASONS['out_of_state']
    }
    if (!userOption.is_active) {
      return DISABLED_REASONS['inactive']
    }

    return null
  }

  careTeam = careTeam ?? []

  const patientCareTeam =
    careTeam.length > 0
      ? providers.filter(
          provider => !provider.test_only && provider.is_active && careTeam.includes(provider.id),
        )
      : []

  // Find all providers(physicians) with practicing states in patient state
  const providersInPatientState = patient
    ? providers.filter(
        provider =>
          providerCanProvideClinicalCareToPatient(provider, patient) &&
          !careTeam.includes(provider.id) &&
          provider.assignee_group != null,
      )
    : []

  let activeCoveringProviders = providers.filter(
    provider =>
      !provider.test_only &&
      provider.is_active &&
      !careTeam.includes(provider.id) &&
      provider.assignee_group != null,
  )
  if (patient) {
    activeCoveringProviders = activeCoveringProviders.filter(
      provider =>
        !providerCanProvideClinicalCareToPatient(provider, patient) &&
        provider.assignee_group != null,
    )
  }

  const inactiveProviders = providers.filter(
    provider => (provider.test_only || !provider.is_active) && provider.assignee_group != null,
  )

  // Deprecated groups are retired from assignment: they stay in the API response so existing
  // assignments can still be labelled, but they are not offered as options here.
  const groupOfUsers = assigneeGroups?.filter(
    group => group.is_group === true && !group.is_deprecated,
  )

  const orderedPatientCareTeam = patientCareTeam.sort((a, b) =>
    (a.first_name ?? '').localeCompare(b.first_name ?? ''),
  )

  const orderedProvidersInPatientState = providersInPatientState.sort((a, b) =>
    (a.first_name ?? '').localeCompare(b.first_name ?? ''),
  )

  const orderedCoveringProviders = activeCoveringProviders.sort((a, b) =>
    (a.first_name ?? '').localeCompare(b.first_name ?? ''),
  )
  const orderedInactiveProviders = inactiveProviders.sort((a, b) =>
    (a.first_name ?? '').localeCompare(b.first_name ?? ''),
  )

  const orderedGroupOfUsers = groupOfUsers?.sort((a, b) => a.name.localeCompare(b.name))
  const groupedGroupOfUsers = orderedGroupOfUsers
    ? [
        {
          label: 'GROUPS',
          options: orderedGroupOfUsers.map(group => ({
            label: `${group.name}`,
            value: group.id || null,
            disabledReason: null,
            category: GROUPS_CATEGORY,
          })),
        },
      ]
    : []

  const groupedCareTeam = careTeam
    ? [
        {
          label: 'CARE TEAM',
          options: orderedPatientCareTeam.map(provider => ({
            label: `${provider.assignee_group?.name}`,
            value: provider.assignee_group?.id ?? null,
            disabledReason: disabledReason(provider),
            category: 'Care Team',
          })),
        },
      ]
    : []

  const groupedProvidersInPatientState = providersInPatientState
    ? [
        {
          label: 'CLINICIANS IN STATE',
          options: orderedProvidersInPatientState.map(provider => ({
            label: `${provider.assignee_group?.name}`,
            value: provider.assignee_group?.id ?? null,
            disabledReason: disabledReason(provider),
            category: 'Clinicians In State',
          })),
        },
      ]
    : []

  const groupedProviders = [
    {
      label: 'COVERING TEAM',
      options: orderedCoveringProviders.map(provider => ({
        label: `${provider.assignee_group?.name}`,
        value: provider.assignee_group?.id ?? null,
        disabledReason: disabledReason(provider),
        category: 'Covering Team',
      })),
    },
    {
      label: 'INACTIVE CARE TEAM',
      options: orderedInactiveProviders.map(provider => ({
        label: `${provider.assignee_group?.name}`,
        value: provider.assignee_group?.id ?? null,
        disabledReason: disabledReason(provider),
        category: 'Inactive Care Team',
      })),
    },
  ]

  let groupedOptions =
    canAssignToPatient && patient
      ? [
          {
            label: 'PATIENT',
            options: [
              {
                label: `${patient?.assignee_group?.name}`,
                value: patient?.assignee_group?.id ?? null,
                category: 'Patient',
              },
            ],
          },
        ]
      : []

  groupedOptions = groupedOptions.concat(
    groupedCareTeam,
    groupedGroupOfUsers,
    groupedProvidersInPatientState,
    groupedProviders,
  )
  return groupedOptions
}

type NamedAssigneeGroup = Pick<AssigneeGroupListItem, 'id' | 'name'>

export const getUserGroupInitials = (group: NamedAssigneeGroup) => {
  const { name = '' } = group
  const splitGroupName = name.split(' ')
  if (!splitGroupName[0]) {
    splitGroupName[0] = ''
  }
  if (!splitGroupName[1]) {
    splitGroupName[1] = ''
  }
  return splitGroupName[0].slice(0, 1) + splitGroupName[1].slice(0, 1)
}

export const getUserGroupMembers = (
  assigneeGroup: NonNullable<Provider['assignee_group']> | AssigneeGroupListItem,
  providers: Pick<Provider, 'id' | 'assignee_group'>[],
) => {
  const members = assigneeGroup.assignee_group_users
  if (members && members.length > 1) {
    return members
      .map(element => providers.find(pr => pr.id == element)?.assignee_group?.name)
      .filter(element => element !== undefined)
  }
  return assigneeGroup.name
}

export const getAssigneeGroupById = (providers: Pick<Provider, 'assignee_group'>[], id: number) => {
  const assigneeGroup = providers.find(
    provider => provider.assignee_group?.id == id,
  )?.assignee_group
  return assigneeGroup ?? undefined
}

export const getAssigneeGroupName = (group: NamedAssigneeGroup) => {
  if (!group) {
    return 'Unknown User'
  }

  const { name } = group
  if (name) {
    return `${name}`
  }
  return ''
}
